/**
 * Module dependencies.
 */

import { Text } from 'src/components/core/text';
import { ifProp, switchProp } from 'styled-tools';
import Svg from 'src/components/core/svg';
import styled, { css } from 'styled-components';
import triangleIcon from 'src/assets/svg/triangle.svg';

/**
 *  `ColorTheme` type.
 */

type ColorTheme = 'dark' | 'light';

/**
 * `Props` type.
 */

type Props = {
  description?: string;
  isScrolled: boolean;
  label: string;
  theme: ColorTheme;
};

/**
 * `ScrollIndicatorWrapper` styled component.
 */

const ScrollIndicatorWrapper = styled.div`
  display: flex;
  transition: var(--transition-default) 0.3s;
  transition-property: opacity, visibility;
  width: max-content;
`;

/**
 * `FootnoteTextWrapper` styled component.
 */

const FootnoteTextWrapper = styled.div`
  display: flex;
  transition: var(--transition-default) 0.3s;
  transition-property: opacity, visibility;
  width: max-content;
`;

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div<{
  isScrolled: boolean;
  theme: ColorTheme;
  visible: boolean;
}>`
  bottom: 2.125rem;
  left: 2.125rem;
  position: fixed;
  visibility: ${ifProp('visible', 'visible', 'hidden')};

  ${switchProp('theme', {
    dark: css`
      color: var(--color-dark);
    `,
    light: css`
      color: var(--color-white);
    `
  })}

  ${ifProp(
    'isScrolled',
    css`
      ${ScrollIndicatorWrapper} {
        opacity: 0;
        position: absolute;
        top: 0;
        transition-delay: 0s;
        visibility: hidden;
      }

      ${FootnoteTextWrapper} {
        opacity: 1;
        visibility: visible;
      }
    `,
    css`
      ${ScrollIndicatorWrapper} {
        opacity: 1;
        visibility: visible;
      }

      ${FootnoteTextWrapper} {
        opacity: 0;
        position: absolute;
        top: 0;
        transition-delay: 0s;
        visibility: hidden;
      }
    `
  )}
`;

/**
 * `FootnoteText` styled component.
 */

const FootnoteText = styled(Text)`
  font-size: 0.9375rem;
  letter-spacing: 0.234rem;
  line-height: 0.9375rem;
  margin: 0;
  text-transform: uppercase;
  white-space: pre-wrap;
`;

/**
 * `ScrollText` styled component.
 */

const ScrollText = styled(Text)`
  color: var(--color-grey);
  font-size: 0.75rem;
  letter-spacing: 0.1875rem;
  line-height: 0.9375rem;
  margin: 0 1rem;
  text-transform: uppercase;
  white-space: pre-wrap;
`;

/**
 * `TriangleIcon` styled component.
 */

const TriangleIcon = styled(Svg).attrs({
  color: '#9a9a9a',
  icon: triangleIcon,
  size: '0.5625rem'
})`
  align-self: center;
`;

/**
 * `LeftTriangleIcon` styled component.
 */

const LeftTriangleIcon = styled(TriangleIcon)`
  transform: rotate(180deg);
`;

/**
 * `SectionFootnote` component.
 */

const SectionFootnote = ({ description, isScrolled, label, theme }: Props) => (
  <Wrapper
    isScrolled={isScrolled}
    theme={theme}
    visible={!!description}
  >
    <ScrollIndicatorWrapper>
      <LeftTriangleIcon />

      <ScrollText
        as={'p'}
        variant={'link'}
      >
        {'SCROLL'}
      </ScrollText>

      <TriangleIcon />
    </ScrollIndicatorWrapper>

    <FootnoteTextWrapper>
      <FootnoteText
        as={'p'}
        variant={'paragraph'}
      >
        {`${label} - `}
      </FootnoteText>

      <FootnoteText
        as={'p'}
        variant={'boldM'}
      >
        {description}
      </FootnoteText>
    </FootnoteTextWrapper>
  </Wrapper>
);

/**
 * Export `SectionFootnote` component.
 */

export default SectionFootnote;
